// apps/web/src/pages/Advertisement/MediaEdit/AdAddMusic.tsx
import React, { useEffect, useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import SmartVideoPlayer from '../../pages/Presentation/SmartVideoPlayer';
import { useMediaPreview } from '../../../shared/contexts/MediaPreviewContext';
import { useTranslation } from 'react-i18next';

// ⬇️ import filter helpers & types
import {
  getWebFilterStyle,
  type FilterPresetId,
} from '../../../shared/components/filterHelpers.web';

type MusicTrack = {
  _id: string;
  title: string;
  artist: string;
  s3Url?: string;
  url?: string;
  audioUrl?: string;
  duration?: number;
  file?: File; // ✅ ADD
};


type LocState = {
  editTargetId?: string;
  remoteUrl?: string;
  sidebarData?: any;
  videoUrl?: string;
};

type SidebarData = {
  hashtags?: string[];
  title?: string;
  description?: string;
  titleColor?: string;
  descriptionColor?: string;
};

const AdAddMusic: React.FC = () => {
  const { t } = useTranslation();

  const [videoPreviewUrl, setVideoPreviewUrl] = useState<string | null>(null);

  const { mediaItems } = useMediaPreview();
  const srcFileRef = useRef<File | null>(null);
  const createdObjectUrlRef = useRef<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
const [customTitle, setCustomTitle] = useState('');
const [customArtist, setCustomArtist] = useState('');
const [titleTouched, setTitleTouched] = useState(false);

  const [uploadedTrack, setUploadedTrack] = useState<{
  file: File;
  url: string;
} | null>(null);

  const { editTargetId, remoteUrl, sidebarData, videoUrl: legacyVideoUrl } =
    ((location.state as any) || {}) as LocState;

  // 🔎 find the media item so we can read its filterId
  const mediaItem = editTargetId
    ? mediaItems.find((m) => m.id === editTargetId)
    : undefined;
  const filterId: FilterPresetId = (mediaItem?.filterId as FilterPresetId) ?? 'none';


  // Preserve prior sidebar values for "Back"
  const prevSidebar: SidebarData =
    (((location.state as any)?.sidebarData) as SidebarData) ?? {};

  const getApiBaseUrl = () => {
    const env = process.env.REACT_APP_API_BASE_URL;
    return env ? env.replace(/\/api\/?$/, '') : 'http://localhost:3001';
  };

  const isHttp = (u?: string | null) => !!u && /^https?:\/\//i.test(u || '');
  const toHttp = (u?: string | null) =>
    !u ? undefined : isHttp(u) ? u : u!.startsWith('/') ? `${getApiBaseUrl()}${u}` : undefined;

  const toAbsoluteHttp = (u?: string | null) => {
    if (!u) return undefined;
    if (/^https?:\/\//i.test(u)) return u;
    if (u.startsWith('/')) return `${getApiBaseUrl()}${u}`;
    return undefined;
  };

  //Helpers for Filtered Media
  // Heuristic: if the file name was produced by VideoFilter/uploadToR2Direct
const looksBakedName = (name?: string) =>
  !!name && /(_filtered_|_baked)\d*\.mp4$/i.test(name);

const fileSignature = (f?: File | null) =>
  f ? `${f.name}|${f.size}|${(f as any).lastModified || 0}` : 'none';

// What the user picked:
const selectedFilterId: FilterPresetId =
  (mediaItem?.filterId as FilterPresetId) ?? 'none';

// If the file is already baked, don't overlay again in the preview
const isBaked = looksBakedName((mediaItem?.file as File | undefined)?.name);
const effectiveFilterId: FilterPresetId = isBaked ? 'none' : selectedFilterId;

const { cssFilter, overlay } = getWebFilterStyle(effectiveFilterId);


  // Resolve source video (prefer File in context)
useEffect(() => {
  const ctxFile = editTargetId
    ? mediaItems.find((m) => m.id === editTargetId)?.file
    : undefined;

  // If the file changed, rebuild object URL
  if (ctxFile instanceof File) {
    srcFileRef.current = ctxFile;

    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
    createdObjectUrlRef.current = URL.createObjectURL(ctxFile);
    setVideoPreviewUrl(createdObjectUrlRef.current);
  } else {
    // fall back to HTTP
    const httpFallback = toAbsoluteHttp(remoteUrl) || toAbsoluteHttp(legacyVideoUrl);
    srcFileRef.current = null;
    // Clean up any previous blob URL
    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
    setVideoPreviewUrl(httpFallback || null);
  }

  // cleanup on unmount
  return () => {
    if (createdObjectUrlRef.current) {
      URL.revokeObjectURL(createdObjectUrlRef.current);
      createdObjectUrlRef.current = null;
    }
  };
  // depend on the actual file identity + fallbacks
}, [editTargetId, mediaItems, remoteUrl, legacyVideoUrl]);


  // ⏺️ Choose a track
  const handleProceed = () => {
  if (!uploadedTrack) return;

  const ctxFile = editTargetId
    ? mediaItems.find((m) => m.id === editTargetId)?.file
    : undefined;

  const httpFallback =
    toHttp(videoPreviewUrl) ||
    toHttp(remoteUrl) ||
    toHttp(legacyVideoUrl);

  navigate('/ad-choose-track', {
    state: {
      editTargetId,
      sidebarData,
      videoFile: ctxFile ?? null,
      remoteUrl: ctxFile ? undefined : httpFallback,
      filterId: selectedFilterId,

      // 👇 synthetic track object
  track: {
  _id: 'custom-upload',

  title: titleTouched
    ? customTitle.trim()     // may be ""
    : uploadedTrack.file.name.replace(/\.[^.]+$/, ''),

  artist: customArtist.trim() || undefined,   // 👈 allow empty
  file: uploadedTrack.file,
},
    },
  });
};

  return (
    <div
      className="video-editor-layout"
      style={{
        position: 'fixed',
        inset: 0,
        height: '100vh',
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: '420px 1fr',
        gap: 16,
      }}
    >
      {/* ◀︎ Back */}
      <button
        onClick={() =>
          navigate('/adupload', {
            state: {
              reset: false, // keep media
              sidebarData: prevSidebar,
            },
          })
        }
        aria-label={t('gallery.addMusic.back')}
        title={t('gallery.addMusic.back')}
        style={{
          position: 'fixed',
          top: 12,
          left: 12,
          zIndex: 1000,

          width: 38,
          height: 38,
          borderRadius: 9999,
          display: 'grid',
          placeItems: 'center',

          border: '1px solid rgba(255,255,255,0.18)',
          background: 'rgba(0,0,0,0.55)',
          color: '#ccc',
          cursor: 'pointer',
          backdropFilter: 'blur(6px)',
          transition:
            'transform 120ms ease, background 120ms ease, border-color 120ms ease',
        }}
        onMouseEnter={(e) => {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.background = 'rgba(0,0,0,0.7)';
          el.style.borderColor = 'rgba(255,255,255,0.28)';
        }}
        onMouseLeave={(e) => {
          const el = e.currentTarget as HTMLButtonElement;
          el.style.background = 'rgba(0,0,0,0.55)';
          el.style.borderColor = 'rgba(255,255,255,0.18)';
        }}
        onMouseDown={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(0.96)';
        }}
        onMouseUp={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M15 19L8 12L15 5"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

     
      <aside
        className="video-editor-sidebar"
        style={{
          display: 'flex',
          flexDirection: 'column',
          borderRight: '1px solid #222',
          padding: 16,
          minWidth: 0,
          overflow: 'hidden',
          background: '#ccc',
        }}
      >
        <h2 style={{ marginBottom: 12 }}>{t('gallery.addMusic.heading')}</h2>

     <div style={{ marginTop: 16 }}>
  <label
    style={{
      display: 'block',
      padding: 20,
      border: '2px dashed #777',
      borderRadius: 12,
      textAlign: 'center',
      cursor: 'pointer',
      background: '#eee',
    }}
  >
    <input
      type="file"
      accept="audio/*"
      hidden
      onChange={(e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  if (!file.type.startsWith('audio/')) {
    alert('Please upload an audio file');
    return;
  }

  if (file.size > 20 * 1024 * 1024) {
    alert('Audio file too large (max 20 MB)');
    return;
  }
const url = URL.createObjectURL(file);
setUploadedTrack({ file, url });

const base = file.name.replace(/\.[^.]+$/, '');
setCustomTitle(base);
setCustomArtist('');
setTitleTouched(false); // 👈 critical
}}

    />

    <div style={{ fontSize: 28 }}>🎵</div>
    <div style={{ marginTop: 8, fontWeight: 600 }}>
      Upload your own music
    </div>
    <div style={{ fontSize: 13, opacity: 0.7 }}>
      MP3, WAV, AAC supported
    </div>
  </label>
</div>

{uploadedTrack && (
  <div style={{ marginTop: 16 }}>
    <strong>{uploadedTrack.file.name}</strong>

    <audio
      src={uploadedTrack.url}
      controls
      style={{ width: '100%', marginTop: 8 }}
    />

    <div style={{ marginTop: 12 }}>
      <label style={{ fontSize: 12, opacity: 0.7 }}>Track title</label>
      <input
        value={customTitle}
  onChange={(e) => {
    setCustomTitle(e.target.value);
    setTitleTouched(true);
  }}
        placeholder="Track title"
         style={{
          width: '100%',
          padding: '8px 10px',
          borderRadius: 8,
          border: '1px solid #ccc',
          marginTop: 4,
        }}
      />
    </div>

    <div style={{ marginTop: 10 }}>
      <label style={{ fontSize: 12, opacity: 0.7 }}>Artist</label>
      <input
        value={customArtist}
        onChange={(e) => setCustomArtist(e.target.value)}
        placeholder="Artist name"
        style={{
          width: '100%',
          padding: '8px 10px',
          borderRadius: 8,
          border: '1px solid #ccc',
          marginTop: 4,
        }}
      />
    </div>
  </div>
)}


 <Button
  disabled={!uploadedTrack}
  onClick={handleProceed}
  style={{
    marginTop: 24,
    backgroundColor: '#2563eb',
    color: 'white',
    width: '100%',
  }}
>
  Continue
</Button>



      </aside>

      <main
        className="video-preview-container"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '12px 20px 20px',
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {videoPreviewUrl ? (
          // ⬇️ Filtered preview container (same visual look as other screens)
          <div
            style={{
              position: 'relative',
              height: '80vh',
              width: 'auto',
              maxWidth: '95%',
              borderRadius: 12,
              overflow: 'hidden',
              background: '#000',
              // apply CSS filter
              filter: cssFilter ?? 'none',
            }}
          >
            <SmartVideoPlayer
              key={`${videoPreviewUrl}|${filterId}`}
              videoUrl={videoPreviewUrl}
              muted
              controls
              containerStyle={{
                height: '100%',
                width: '100%',
                borderRadius: 0, // container already rounded
                overflow: 'hidden',
                background: 'transparent',
              }}
              videoStyle={{ height: '100%', width: '100%', objectFit: 'contain' }}
            />

            {/* tint overlay if present */}
            {!!overlay?.tint && (overlay.opacity ?? 0) > 0 && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: overlay.tint,
                  opacity: overlay.opacity,
                  mixBlendMode: filterId === 'noir' ? 'multiply' : 'overlay',
                  pointerEvents: 'none',
                }}
              />
            )}
          </div>
        ) : (
          <p className="text-gray-500">{t('gallery.addMusic.noVideo')}</p>
        )}
      </main>
    </div>
  );
};

export default AdAddMusic;
